import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, Download, Mail } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | timeout | error
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        console.error(e);
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const downloadUrl = details?.download_url;
  const downloadFilename = details?.download_filename;
  const productName = details?.product_name;

  return (
    <main
      data-testid="success-page"
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6 font-body relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-emerald-500/15 blur-[140px] -z-10"
      />
      <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 md:p-12 text-center glow-emerald">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-6" />
            <h1 className="font-display font-bold text-white text-3xl tracking-tight">
              Confirming your payment…
            </h1>
            <p className="mt-3 text-white/60">
              Hang tight, this usually takes a few seconds.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-6" />
            <h1
              data-testid="success-title"
              className="font-display font-extrabold text-white text-4xl md:text-5xl tracking-tighter"
            >
              You're in. <span className="text-emerald-400">Welcome.</span>
            </h1>
            {productName && (
              <p className="mt-4 text-white/70 text-base md:text-lg">
                Your purchase of{" "}
                <strong className="text-white">{productName}</strong> is confirmed.
              </p>
            )}
            <p className="mt-2 text-white/50 text-sm">
              Download your files below — save them somewhere safe.
            </p>

            {downloadUrl ? (
              <a
                href={downloadUrl}
                download={downloadFilename || true}
                data-testid="download-btn"
                className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-emerald-500 text-black font-display font-bold uppercase tracking-wide hover:bg-emerald-400 hover:shadow-[0_0_60px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all"
              >
                <Download className="w-4 h-4" /> Download Your Copy
              </a>
            ) : (
              <div className="mt-8 text-sm text-amber-300/80">
                Preparing your download link…
              </div>
            )}

            {downloadFilename && (
              <div className="mt-4 text-[11px] text-white/40 font-mono break-all">
                {downloadFilename}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-white/10 text-xs text-white/50 flex items-center justify-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              A receipt has been emailed to you by Stripe.
            </div>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <XCircle className="w-14 h-14 text-amber-400 mx-auto mb-6" />
            <h1 className="font-display font-bold text-white text-3xl tracking-tight">
              {status === "expired"
                ? "Checkout session expired"
                : status === "timeout"
                ? "Still processing…"
                : "Something went wrong"}
            </h1>
            <p className="mt-3 text-white/60">
              {status === "timeout"
                ? "We couldn't confirm your payment yet. Please check your email — if charged, you'll receive a receipt shortly."
                : "Please head back to the homepage and try again."}
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="back-home-link"
          className="mt-8 inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to homepage
        </Link>
        {sessionId && (
          <div className="mt-6 text-[11px] text-white/25 font-mono break-all">
            session: {sessionId}
          </div>
        )}
      </div>
    </main>
  );
}
